import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Clock, Loader2, Timer } from "lucide-react";
import { toast } from "sonner";

import { Container } from "@/platform/layout/container";
import { Button } from "@/platform/ui/button";
import { Badge } from "@/platform/ui/badge";
import { Card, CardContent } from "@/platform/ui/card";
import { useSession } from "@/platform/auth/use-session";
import {
  computeSimulationSummary,
  endSimulationSession,
  loadOpenSimulationSession,
  recordSimulationAttempt,
  startSimulationSession,
  type SimulationQuestion,
  type SimulationSummary,
} from "@/tenants/einstein/features/simulation/api";
import { Blob, SoftStar } from "@/platform/ui/illustrations/blobs";
import { TrophySpot } from "@/platform/ui/illustrations/spot";
import { cn } from "@/platform/lib/cn";

type Phase = "intro" | "running" | "finishing" | "done";

const TARGET_TOTAL_MIN = 300; // 5h

const DISCIPLINE_LABELS: Record<string, string> = {
  portugues: "Português",
  ingles: "Inglês",
  matematica: "Matemática",
  biologia: "Biologia",
  quimica: "Química",
  fisica: "Física",
  historia: "História",
  geografia: "Geografia",
};

export function SimulationPage() {
  const navigate = useNavigate();
  const { session } = useSession();
  const [phase, setPhase] = useState<Phase>("intro");
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [questions, setQuestions] = useState<SimulationQuestion[]>([]);
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [summary, setSummary] = useState<SimulationSummary | null>(null);
  const startedAtRef = useRef<number>(Date.now());
  const questionStartRef = useRef<number>(Date.now());
  const [loading, setLoading] = useState(false);
  const [resuming, setResuming] = useState(true);
  const resumeAttemptedRef = useRef(false);

  // Retomar sessão aberta ao montar. Sem isso, refresh/fechar aba em sessão
  // de 5h faz a aluna perder o progresso visual e ver phase=intro.
  useEffect(() => {
    if (!session) return;
    if (resumeAttemptedRef.current) return;
    resumeAttemptedRef.current = true;
    (async () => {
      try {
        const open = await loadOpenSimulationSession(session.user.id);
        if (open) {
          setSessionId(open.session.id);
          setQuestions(open.questions);
          setAnswers(open.answers);
          // Posiciona no primeiro não respondido.
          const firstUnanswered = open.questions.findIndex((q) => !open.answers[q.id]);
          setIndex(firstUnanswered === -1 ? open.questions.length - 1 : firstUnanswered);
          startedAtRef.current = new Date(open.session.started_at).getTime();
          questionStartRef.current = Date.now();
          setPhase("running");
          toast.success("Retomando seu simulado de onde parou.");
        }
      } catch (err) {
        console.error("[simulation] resume failed", err);
      } finally {
        setResuming(false);
      }
    })();
  }, [session]);

  async function startSimulation() {
    if (!session) return;
    setLoading(true);
    try {
      const { session: sim, questions: qs } = await startSimulationSession(session.user.id);
      if (qs.length === 0) {
        toast.error("Sem questões disponíveis pro simulado.");
        navigate("/painel", { replace: true });
        return;
      }
      setSessionId(sim.id);
      setQuestions(qs);
      startedAtRef.current = Date.now();
      questionStartRef.current = Date.now();
      setPhase("running");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Não foi possível iniciar.");
    } finally {
      setLoading(false);
    }
  }

  const currentQuestion = questions[index];
  const isLast = index === questions.length - 1;
  const objectivesCount = questions.filter((q) => q.kind === "objective").length;

  const finalize = useCallback(async () => {
    if (!sessionId) return;
    setPhase("finishing");
    try {
      const sm = await computeSimulationSummary(sessionId);
      await endSimulationSession(sessionId, {
        rawScore: sm.raw_score,
        durationSeconds: sm.duration_seconds,
        byDiscipline: sm.by_discipline,
      });
      setSummary(sm);
      setPhase("done");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Falha ao finalizar.");
      setPhase("done");
    }
  }, [sessionId]);

  const advance = useCallback(
    async (skip = false) => {
      if (!currentQuestion || !session || !sessionId) return;
      const answer = skip ? "" : answers[currentQuestion.id] ?? "";
      if (!skip && answer.trim().length === 0) {
        toast.error("Responda ou pule a questão.");
        return;
      }
      const timeSpent = Date.now() - questionStartRef.current;
      if (!skip) {
        try {
          await recordSimulationAttempt({
            userId: session.user.id,
            sessionId,
            questionId: currentQuestion.id,
            answer,
            correctAnswer: currentQuestion.correct_answer,
            kind: currentQuestion.kind,
            timeSpentMs: timeSpent,
          });
        } catch (err) {
          console.warn("[simulation] attempt save failed", err);
        }
      }
      if (isLast) {
        await finalize();
      } else {
        setIndex((i) => i + 1);
        questionStartRef.current = Date.now();
      }
    },
    [answers, currentQuestion, finalize, isLast, session, sessionId],
  );

  if (phase === "intro") {
    if (resuming) {
      return (
        <FullPageState
          icon={<Loader2 className="size-10 animate-spin text-[color:var(--color-accent)]" aria-hidden />}
          title="Verificando seu progresso…"
          text="Procurando um simulado em aberto."
        />
      );
    }
    return <IntroView onStart={startSimulation} loading={loading} />;
  }
  if (phase === "finishing") {
    return (
      <FullPageState
        icon={<Loader2 className="size-10 animate-spin text-[color:var(--color-accent)]" aria-hidden />}
        title="Calculando seu desempenho…"
        text="Cruzando as respostas com a rubrica e a distribuição da prova."
      />
    );
  }
  if (phase === "done" && summary) {
    return <SummaryView summary={summary} onContinue={() => navigate("/painel")} />;
  }
  if (!currentQuestion) return null;

  return (
    <div className="relative overflow-hidden bg-mesh min-h-[calc(100vh-56px)]">
      <Container size="default" className="relative py-10 md:py-12">
        <header className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            <Badge variant={currentQuestion.kind === "discursive" ? "pink" : "accent"}>
              {currentQuestion.kind === "discursive" ? "Discursiva" : "Objetiva"} ·{" "}
              {index + 1}/{questions.length}
            </Badge>
            <h1 className="mt-3 font-display text-[length:var(--text-h2)] sm:text-[length:var(--text-h1)] font-semibold leading-[var(--text-h1--line-height)] tracking-[var(--text-h1--letter-spacing)]">
              {DISCIPLINE_LABELS[currentQuestion.discipline] ?? currentQuestion.discipline}
            </h1>
          </div>
          <div className="shrink-0">
            <SessionTimer startedAt={startedAtRef.current} target={TARGET_TOTAL_MIN * 60} />
          </div>
        </header>

        <Progress current={index + 1} total={questions.length} objectives={objectivesCount} />

        <Card className="mt-6 p-7 md:p-8">
          <CardContent className="p-0 space-y-6">
            {currentQuestion.has_base_text && currentQuestion.base_text ? (
              <details className="rounded-lg border border-[--color-border] bg-[color:var(--color-paper)] p-5 text-[length:var(--text-body-sm)]">
                <summary className="cursor-pointer font-semibold">
                  Texto-base · clique para ler
                </summary>
                <div className="mt-3 whitespace-pre-wrap text-[color:var(--color-muted-foreground)]">
                  {currentQuestion.base_text}
                </div>
              </details>
            ) : null}

            <div className="whitespace-pre-wrap text-[length:var(--text-body)] leading-relaxed">
              {currentQuestion.stem}
            </div>

            {currentQuestion.kind === "objective" && currentQuestion.alternatives ? (
              <fieldset className="space-y-2.5">
                {currentQuestion.alternatives.map((alt) => {
                  const isSelected = answers[currentQuestion.id] === alt.label;
                  return (
                    <label
                      key={alt.label}
                      className={cn(
                        "flex cursor-pointer items-start gap-3 rounded-lg border-2 p-4 transition-colors duration-150 ease-out",
                        isSelected
                          ? "border-[color:var(--color-accent)] bg-[color:var(--color-accent-soft)]"
                          : "border-[--color-border] hover:border-[--color-accent]/40 hover:bg-[color:var(--color-paper)]",
                      )}
                    >
                      <span
                        className={cn(
                          "mt-0.5 grid size-6 place-items-center rounded-full text-[length:var(--text-caption)] font-bold shrink-0",
                          isSelected
                            ? "bg-[color:var(--color-accent)] text-white"
                            : "bg-[color:var(--color-muted)] text-[color:var(--color-muted-foreground)]",
                        )}
                      >
                        {alt.label}
                      </span>
                      <input
                        type="radio"
                        name={`alt-${currentQuestion.id}`}
                        value={alt.label}
                        checked={isSelected}
                        onChange={() =>
                          setAnswers((a) => ({ ...a, [currentQuestion.id]: alt.label }))
                        }
                        className="sr-only"
                      />
                      <span className="flex-1 text-[length:var(--text-body-sm)] leading-relaxed">
                        {alt.text}
                      </span>
                    </label>
                  );
                })}
              </fieldset>
            ) : (
              <textarea
                value={answers[currentQuestion.id] ?? ""}
                onChange={(e) =>
                  setAnswers((a) => ({ ...a, [currentQuestion.id]: e.target.value }))
                }
                rows={8}
                placeholder="Resposta técnica. Mostre o caminho — justificativa pontua tanto quanto resposta certa."
                className="w-full rounded-lg border border-[--color-border] bg-[color:var(--color-card)] p-4 text-[length:var(--text-body)] leading-relaxed focus:border-[color:var(--color-accent)] focus:outline-none focus:ring-4 focus:ring-[--color-ring]"
              />
            )}
          </CardContent>
        </Card>

        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <Button variant="ghost" size="md" onClick={() => advance(true)} className="order-2 sm:order-1">
            Pular
          </Button>
          <Button size="xl" onClick={() => advance(false)} className="order-1 sm:order-2 w-full sm:w-auto">
            {isLast ? "Finalizar simulado" : "Próxima"}
            <ArrowRight className="size-4" aria-hidden />
          </Button>
        </div>
      </Container>
    </div>
  );
}

function Progress({
  current,
  total,
  objectives,
}: {
  current: number;
  total: number;
  objectives: number;
}) {
  const pct = (current / total) * 100;
  const objPct = (objectives / total) * 100;
  return (
    <div className="mt-5">
      <div className="relative h-2 w-full overflow-hidden rounded-full bg-[color:var(--color-muted)]">
        {/* marker for objective/discursive boundary */}
        <div
          className="absolute inset-y-0 w-px bg-[color:var(--color-accent)]/40"
          style={{ left: `${objPct}%` }}
          aria-hidden
        />
        <div
          className="h-full bg-gradient-accent transition-[width] duration-200"
          style={{ width: `${pct}%` }}
          aria-hidden
        />
      </div>
      <p className="mt-1 text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]">
        <span data-tabular="true">{objectives}</span> objetivas ·{" "}
        <span data-tabular="true">{total - objectives}</span> discursivas
      </p>
    </div>
  );
}

function SessionTimer({ startedAt, target }: { startedAt: number; target: number }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const secs = Math.floor((now - startedAt) / 1000);
  const remaining = Math.max(0, target - secs);
  const hh = String(Math.floor(remaining / 3600)).padStart(2, "0");
  const mm = String(Math.floor((remaining % 3600) / 60)).padStart(2, "0");
  const ss = String(remaining % 60).padStart(2, "0");
  const isLow = remaining < 30 * 60; // <30 min
  return (
    <div
      className={cn(
        "inline-flex items-center gap-2 rounded-full border px-4 py-2 font-mono text-[length:var(--text-body-sm)] font-semibold shadow-[var(--shadow-xs)]",
        isLow
          ? "border-[color:var(--color-pink)] bg-[color:var(--color-pink-soft)] text-[color:var(--color-pink-deep)]"
          : "border-[--color-border] bg-[color:var(--color-card)]",
      )}
    >
      <Clock className="size-3.5" aria-hidden />
      <span data-tabular="true">
        {hh}:{mm}:{ss}
      </span>
    </div>
  );
}

function IntroView({ onStart, loading }: { onStart: () => void; loading: boolean }) {
  return (
    <div className="relative overflow-hidden bg-mesh min-h-[calc(100vh-56px)]">
      <Blob
        color="violet"
        className="pointer-events-none absolute -left-24 -top-16 w-96 opacity-30 blur-2xl animate-blob"
      />
      <Blob
        color="pink"
        className="pointer-events-none absolute -right-32 top-60 w-96 opacity-25 blur-2xl animate-blob"
      />
      <SoftStar
        color="yellow"
        className="pointer-events-none absolute right-10 top-32 w-12 opacity-60 animate-float"
      />

      <Container size="default" className="relative py-20 md:py-24">
        <header className="animate-fade-up max-w-2xl text-center mx-auto">
          <Badge variant="accentSolid" className="mx-auto w-fit">
            <Timer className="size-3" aria-hidden />
            Simulado completo · 5h
          </Badge>
          <h1 className="mt-6 font-display text-[length:var(--text-display-lg)] font-semibold leading-[var(--text-display-lg--line-height)] tracking-[var(--text-display-lg--letter-spacing)] text-balance">
            Cinco horas no <span className="text-gradient-accent">formato Einstein.</span>
          </h1>
          <p className="mt-5 text-[length:var(--text-body-lg)] text-[color:var(--color-muted-foreground)] leading-[var(--text-body-lg--line-height)] text-pretty">
            50 objetivas balanceadas por disciplina, 5 discursivas (Bio · Quím · Fís · Mat · Inglês),
            cronômetro rigoroso. Sem pausas — fidelidade ao dia da prova.
          </p>
        </header>

        <Card className="mt-12 p-8 md:p-10 mx-auto max-w-3xl">
          <div className="grid items-center gap-8 md:grid-cols-[1fr_auto]">
            <ul className="space-y-3 text-[length:var(--text-body-sm)]">
              {[
                { tone: "violet" as const, text: "50 objetivas balanceadas (Português, Mat, Bio, Quím, Fís, Inglês, Hist, Geo)" },
                { tone: "pink" as const, text: "5 discursivas (Bio · Quím · Fís · Mat · Inglês)" },
                { tone: "mint" as const, text: "Cronômetro de 5h regressivo, alerta visual nos últimos 30 min" },
                { tone: "yellow" as const, text: "Resultado: pontuação objetiva + nota padronizada estimada" },
              ].map((it) => (
                <li key={it.text} className="flex items-start gap-3">
                  <span className={`mt-0.5 size-2 rounded-full bg-[color:var(--color-${it.tone === "violet" ? "accent" : it.tone === "yellow" ? "yellow-deep" : it.tone})] shrink-0`} aria-hidden />
                  <span>{it.text}</span>
                </li>
              ))}
            </ul>
            <TrophySpot className="size-32 hidden md:block animate-float" />
          </div>

          <div className="mt-8 rounded-lg border border-[color:var(--color-warning)]/30 bg-[color:var(--color-warning-soft)] p-4 text-[length:var(--text-body-sm)] text-[color:var(--color-warning)]">
            <p className="font-semibold">Antes de começar</p>
            <p className="mt-1">
              Reserve 5 horas ininterruptas. Quando começar, o cronômetro não pausa. Pode pular
              questões e voltar depois — todas suas respostas ficam salvas.
            </p>
          </div>

          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Button size="xl" variant="gradient" onClick={onStart} loading={loading}>
              Começar agora
              <ArrowUpRight className="size-4" aria-hidden />
            </Button>
          </div>
        </Card>
      </Container>
    </div>
  );
}

function FullPageState({
  icon,
  title,
  text,
}: {
  icon: React.ReactNode;
  title: string;
  text: string;
}) {
  return (
    <div className="relative overflow-hidden bg-mesh min-h-[calc(100vh-56px)] grid place-items-center">
      <div className="text-center">
        <div className="mx-auto inline-block">{icon}</div>
        <h1 className="mt-6 font-display text-[length:var(--text-display-md)] font-semibold">
          {title}
        </h1>
        <p className="mt-3 text-[length:var(--text-body)] text-[color:var(--color-muted-foreground)] max-w-md mx-auto">
          {text}
        </p>
      </div>
    </div>
  );
}

function SummaryView({
  summary,
  onContinue,
}: {
  summary: SimulationSummary;
  onContinue: () => void;
}) {
  const hours = Math.floor(summary.duration_seconds / 3600);
  const minutes = Math.floor((summary.duration_seconds % 3600) / 60);
  const objAccuracy = summary.objectives_total > 0
    ? Math.round((summary.objectives_correct / summary.objectives_total) * 100)
    : 0;
  const standardizedEstimate = useMemo(() => {
    // Nota padronizada estimada: média 500, DP 100. Mapeia objAccuracy 50→500, 70→600, etc.
    return Math.round(500 + (objAccuracy - 50) * 5);
  }, [objAccuracy]);

  return (
    <div className="relative overflow-hidden bg-mesh">
      <Blob
        color="violet"
        className="pointer-events-none absolute -left-24 -top-16 w-96 opacity-30 blur-2xl animate-blob"
      />
      <SoftStar
        color="yellow"
        className="pointer-events-none absolute right-12 top-24 w-12 opacity-60 animate-float"
      />

      <Container size="default" className="relative py-14 md:py-20">
        <Badge variant="accentSolid">Simulado concluído</Badge>
        <h1 className="mt-4 font-display text-[length:var(--text-display-lg)] font-semibold leading-[var(--text-display-lg--line-height)] tracking-[var(--text-display-lg--letter-spacing)] text-balance">
          <span data-tabular="true" className="text-gradient-accent">
            {summary.objectives_correct} de {summary.objectives_total}
          </span>{" "}
          objetivas.
        </h1>
        <p className="mt-3 text-[length:var(--text-body-lg)] text-[color:var(--color-muted-foreground)] leading-[var(--text-body-lg--line-height)] text-pretty">
          {objAccuracy}% de acerto em {hours}h {minutes}min. Discursivas precisam de correção
          separada — envie pelo módulo /discursivas.
        </p>

        <div className="mt-10 grid gap-4 md:grid-cols-3">
          <MetricLarge tone="violet" label="Nota objetiva" value={`${summary.objectives_correct}/${summary.objectives_total}`} />
          <MetricLarge
            tone="pink"
            label="Padronizada (est.)"
            value={String(standardizedEstimate)}
            sub="média 500 · DP 100"
          />
          <MetricLarge
            tone="mint"
            label="Tempo total"
            value={`${hours}h ${minutes}m`}
            sub={`de 5h disponíveis`}
          />
        </div>

        {Object.keys(summary.by_discipline).length > 0 ? (
          <div className="mt-10 grid gap-4 md:grid-cols-2">
            {Object.entries(summary.by_discipline)
              .sort((a, b) => {
                const accA = a[1].total > 0 ? a[1].correct / a[1].total : 0;
                const accB = b[1].total > 0 ? b[1].correct / b[1].total : 0;
                return accA - accB;
              })
              .map(([disc, stats]) => {
                const pct = stats.total > 0 ? Math.round((stats.correct / stats.total) * 100) : 0;
                const isWeak = pct < 50;
                return (
                  <Card key={disc} className="p-6">
                    <div className="flex items-center justify-between">
                      <p className="font-display text-[length:var(--text-h4)] font-semibold">
                        {DISCIPLINE_LABELS[disc] ?? disc}
                      </p>
                      <Badge variant={isWeak ? "pink" : "mint"}>
                        {stats.correct}/{stats.total}
                      </Badge>
                    </div>
                    <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-[color:var(--color-muted)]">
                      <div
                        className={`h-full rounded-full transition-[width] duration-700 ${isWeak ? "bg-[color:var(--color-pink)]" : "bg-[color:var(--color-mint)]"}`}
                        style={{ width: `${pct}%` }}
                        aria-hidden
                      />
                    </div>
                    <p className="mt-2 text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]">
                      <span data-tabular="true">{pct}%</span> de acerto
                    </p>
                  </Card>
                );
              })}
          </div>
        ) : null}

        <div className="mt-10 flex flex-wrap gap-3">
          <Button size="xl" variant="gradient" onClick={onContinue}>
            Voltar ao painel
            <ArrowUpRight className="size-4" aria-hidden />
          </Button>
        </div>
      </Container>
    </div>
  );
}

function MetricLarge({ tone, label, value, sub }: { tone: "violet" | "pink" | "mint"; label: string; value: string; sub?: string }) {
  const badgeVariant = tone === "violet" ? "accent" : tone;
  return (
    <Card className="p-6">
      <Badge variant={badgeVariant}>{label}</Badge>
      <p
        data-tabular="true"
        className="mt-4 font-display text-[length:var(--text-display-md)] font-bold leading-none tracking-[var(--text-display-md--letter-spacing)]"
      >
        {value}
      </p>
      {sub ? (
        <p className="mt-2 text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]">
          {sub}
        </p>
      ) : null}
    </Card>
  );
}
